import { ReactElement, useState, useEffect } from "react";
import wsgLogoMembershipDues from "../../assets/images&icons/WSGLogoMembershipDues.png";
import "./AboutUs.css";
import "./MembershipDues.css";
import GeneralLayoutWithDots from "./components/GeneralLayoutWithDots";
import { getFeesByFyYearAndType, IFees } from "../../Services/Fees";

const MEMBERSHIP_DUES_FY_YEAR = 2027;

export default function MembershipDues(): ReactElement {
  const [legalFees, setLegalFees] = useState<IFees[]>([]);
  const [bankingFees, setBankingFees] = useState<IFees | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFees = async () => {
      try {
        const legalData = await getFeesByFyYearAndType(
          MEMBERSHIP_DUES_FY_YEAR,
          "legal",
        );
        setLegalFees(legalData);

        const bankingTypes = ["banking", "investment", "Investment Banking"];
        let bankingRow: IFees | null = null;
        for (const type of bankingTypes) {
          const bankingData = await getFeesByFyYearAndType(
            MEMBERSHIP_DUES_FY_YEAR,
            type,
          );
          if (bankingData?.length) {
            bankingRow = bankingData[0];
            break;
          }
        }
        setBankingFees(bankingRow);
      } catch (error) {
        console.error("Error fetching fees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFees();
  }, []);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
      <div className="w-full membership-dues-page">
        <GeneralLayoutWithDots
          CurrentPage="MembershipDues"
        >
          <>
             <div className="membership-dues-layout-content">
              <div 
                className="bg-[#BC3323] text-white pt-12 xs:pt-16 sm:pt-20 md:pt-24 lg:pt-[80px] pb-10 xs:pb-12 sm:pb-14 md:pb-16 lg:pb-[60px] bg-no-repeat bg-right"
                style={{ backgroundImage: `url(${wsgLogoMembershipDues})` }}
              >
                <div className="membership-dues-banner-content">
                  <p className="text-left text-white membership-dues-title mb-[20px]">
                    WSG membership offers global expertise powered by digital innovation and includes membership for all firm professionals providing unequalled value compared to the traditional competing network. Member fees are set by the Board of Directors and are classified by service industry and jurisdiction representation.
                    <br /><br />
                    For more information about WSG membership contact{" "}
                    <a 
                      href="mailto:info@worldservicesgroup.com" 
                      className="text-white hover:text-gray-200 underline"
                    >
                      info@worldservicesgroup.com
                    </a>
                  </p>
                </div>
              </div>
             </div>
            
            <div className="px-4 xxs:px-5 xs:px-6 sm:px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-[70px] -mt-4 xs:-mt-5 sm:-mt-6 md:-mt-[30px]">
             <div className="pt-2 xs:pt-3 sm:pt-[11px] pb-4 xs:pb-5 sm:pb-[20px]">
                 <div className="flex flex-wrap gap-3 xs:gap-4 sm:gap-6 md:gap-8 text-sm xs:text-base sm:text-lg font-medium">
                   <a href="#legal" className="nav-link-hover text-[#474F56] hover:text-[#BC3323] transition-colors">
                     Legal Service Fees
                   </a>
                   <span className="text-[#474F56] hidden xs:inline">|</span>
                   <a href="#accounting" className="nav-link-hover text-[#474F56] hover:text-[#BC3323] transition-colors">
                     Accounting Service Fees
                   </a>
                   <span className="text-[#474F56] hidden xs:inline">|</span>
                   <a href="#banking" className="nav-link-hover text-[#474F56] hover:text-[#BC3323] transition-colors">
                     Investment Banking Service Fees
                   </a>
                 </div>
             </div>

             <div className="border-b border-gray-200 mb-6 xs:mb-7 sm:mb-8 md:mb-[30px]"></div>


            <div id="legal" className="mb-8 xs:mb-9 sm:mb-10 md:mb-12">
              <div className="bg-[#BC3323] mb-6">
                <h2 className="membership-dues-title-box uppercase">Legal Service Fees</h2>
              </div>
              <div>
                <p className="membership-dues-paragraph">
                  Dues and initiation fees are based upon the total number of directors, partners, associates, counsel, managers and trainees in the jurisdiction being represented.
                </p>
              </div>
              
              <div className="px-2 xs:px-3 sm:px-4 md:px-6 lg:px-8 membership-dues-tables-grid">
                <div className="bg-white">
                  <h3 className="membership-dues-table-title">One-time Initiation Fee</h3>
                  <div className="overflow-x-auto">
                    <table className="membership-dues-table">
                      <thead>
                        <tr>
                          <th># of Professionals</th>
                          <th>Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={2}>Loading...</td>
                          </tr>
                        ) : legalFees.length === 0 ? (
                          <tr>
                            <td colSpan={2}>No data available</td>
                          </tr>
                        ) : (
                          legalFees.map((fee, index) => (
                            <tr key={fee.id || index}>
                              <td>{fee.details}</td>
                              <td>{formatCurrency(fee.fees)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white">
                  <h3 className="membership-dues-table-title">Yearly Membership Dues</h3>
                  <div className="overflow-x-auto">
                    <table className="membership-dues-table">
                      <thead>
                        <tr>
                          <th># of Professionals</th>
                          <th>Dues</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={2}>Loading...</td>
                          </tr>
                        ) : legalFees.length === 0 ? (
                          <tr>
                            <td colSpan={2}>No data available</td>
                          </tr>
                        ) : (
                          legalFees.map((fee, index) => (
                            <tr key={fee.id || index}>
                              <td>{fee.details}</td>
                              <td>{formatCurrency(fee.dues)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div id="accounting" className="mb-8 xs:mb-9 sm:mb-10 md:mb-12">
              <div className="bg-[#BC3323] mb-6">
                <h2 className="membership-dues-title-box uppercase">Accounting Service Fees</h2>
              </div>
              <div className="px-2 xs:px-3 sm:px-4 md:px-6 lg:px-8">
                <p className="membership-dues-paragraph">
                  Dues and initiation fees are based upon the total number of directors, partners, managers and senior accountants in the jurisdiction being represented. They do not include junior accountants or trainees.
                </p>
              </div>
              
              <div className="px-2 xs:px-3 sm:px-4 md:px-6 lg:px-8 membership-dues-tables-grid">
                <div className="bg-white">
                  <h3 className="membership-dues-table-title">One-time Initiation Fee</h3>
                  <div className="overflow-x-auto">
                    <table className="membership-dues-table">
                      <thead>
                        <tr>
                          <th># of Professionals</th>
                          <th>Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={2}>Loading...</td>
                          </tr>
                        ) : legalFees.length === 0 ? (
                          <tr>
                            <td colSpan={2}>No data available</td>
                          </tr>
                        ) : (
                          legalFees.map((fee, index) => (
                            <tr key={fee.id || index}>
                              <td>{fee.details}</td>
                              <td>{formatCurrency(fee.fees)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white">
                  <h3 className="membership-dues-table-title">Yearly Membership Dues</h3>
                  <div className="overflow-x-auto">
                    <table className="membership-dues-table">
                      <thead>
                        <tr>
                          <th># of Professionals</th>
                          <th>Dues</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={2}>Loading...</td>
                          </tr>
                        ) : legalFees.length === 0 ? (
                          <tr>
                            <td colSpan={2}>No data available</td>
                          </tr>
                        ) : (
                          legalFees.map((fee, index) => (
                            <tr key={fee.id || index}>
                              <td>{fee.details}</td>
                              <td>{formatCurrency(fee.dues)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div id="banking" className="mb-8 xs:mb-9 sm:mb-10 md:mb-12">
              <div className="bg-[#BC3323] mb-6">
                <h2 className="membership-dues-title-box uppercase">Investment Banking Service Fees</h2>
              </div>
              <div className="px-2 xs:px-3 sm:px-4 md:px-6 lg:px-8">
                <p className="membership-dues-paragraph">
                  Dues and initiation fees are based upon the total number of directors, partners, managers and senior accountants in the jurisdiction being represented. They do not include junior accountants or trainees.
                </p>
              </div>
              
              <div className="px-2 xs:px-3 sm:px-4 md:px-6 lg:px-8 membership-dues-tables-grid">
                <div className="bg-white">
                  <h3 className="membership-dues-table-title">One-time Initiation Fee</h3>
                  <div className="overflow-x-auto">
                    <table className="membership-dues-table">
                      <thead>
                        <tr>
                          <th>Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td>Loading...</td>
                          </tr>
                        ) : !bankingFees ? (
                          <tr>
                            <td>No data available</td>
                          </tr>
                        ) : (
                          <tr>
                            <td>{formatCurrency(bankingFees.fees)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white">
                  <h3 className="membership-dues-table-title">Yearly Membership Dues</h3>
                  <div className="overflow-x-auto">
                    <table className="membership-dues-table">
                      <thead>
                        <tr>
                          <th>Dues</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td>Loading...</td>
                          </tr>
                        ) : !bankingFees ? (
                          <tr>
                            <td>No data available</td>
                          </tr>
                        ) : (
                          <tr>
                            <td>{formatCurrency(bankingFees.dues)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </>
        </GeneralLayoutWithDots>
      </div>
  );
}
